import { type IGatsbyImageData } from 'gatsby-plugin-image';
import { getGatsbyImageData } from '@kontent-ai/gatsby-components/dist/image-element/get-gatsby-image-data';
import { type ImageItem } from '@kontent-ai/gatsby-components/dist/image-element';

/**
 * `1` = image uploaded in Kontent cannot be resized for high-resolution screens (Retina)
 * All devices/screens will receive the image in the same pixel density and dimensions.
 * => 1 image is created
 *
 * `2` = image uploaded in Kontent does support high-resolution screens (Retina).
 * Retina screens will be served the unmodified image, other screens will receive
 * the image in half dimensions.
 * => 2 images are created
 */
type ImageDPR = 1 | 2;

export interface ImageByDPR {
  dpr: ImageDPR;
  image: IGatsbyImageData;
}

interface Props {
  image: ImageItem;
  id: string;
  sourceImageDPR: ImageDPR;
}

const getImage = ({ image, id, sourceImageDPR }: Props): ImageByDPR[] => {
  const result: ImageByDPR[] = [];

  for (let dpr = 1; dpr <= sourceImageDPR; dpr += 1) {
    const dprFraction = sourceImageDPR / dpr;

    const imageData = getGatsbyImageData({
      image: {
        ...image,
        description: image.description || '',
        image_id: `${id}-dpr-${dpr}`,
      },
      width: Math.round((image.width || 1) / dprFraction),
      height: Math.round((image.height || 1) / dprFraction),
    });

    result.push({
      dpr: dpr as ImageDPR,
      image: imageData,
    });
  }

  return result;
};

export default getImage;
